/* ==========================================================================
   GemofU — collection filtering and sorting
   Loaded after gou-search.js from layout/theme.liquid.

   What Forge gives us, and what was wrong with it:

   1. Sort lives only inside the filter drawer, behind two taps, so nobody
      found "Alphabetically, A-Z". This file lifts the same options the drawer
      already holds into a select in the collection toolbar, so sorting is one
      tap on a phone and one click on a desktop.

   2. Forge serialises the filter form on every click, but the active sort
      option is never re-checked when the grid comes back from the server — so
      picking a colour silently reset the sort to Featured. A capture-phase
      listener re-attaches the current sort before the theme's own handler
      reads the form.

   3. The drawer uses checkboxes for sort, so two sort_by values could go out
      together. The same listener clears the others.

   4. Colour swatches fall back to `background-color: <last word of the
      label>`. That happens to work for "Green" and paints nothing at all for
      "Ruby", "Kemp" or "Oxidised" — which is why Ruby rendered as a grey
      blank. The table below maps this catalogue's colour vocabulary to real
      values.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Colour vocabulary. Keys are the filter label, lower-cased. Values are
     anything valid in CSS `background` — a hex, or a gradient for the stones
     that genuinely are more than one colour.
     ------------------------------------------------------------------------ */
  var COLOUR = {
    'ruby': '#9b111e',
    'deep ruby': '#7d0d1b',
    'ruby red': '#9b111e',
    'kemp': '#a8121f',
    'kemp red': '#a8121f',
    'red': '#c02026',
    'maroon': '#7b2230',
    'wine': '#722f37',
    'emerald': '#0f6b4f',
    'green': '#2e7d4f',
    'mint green': '#a8e0c0',
    'green amethyst': '#9fc3a4',
    'olive': '#7a7b3a',
    'blue': '#1f5fa9',
    'royal blue': '#153a8a',
    'navy': '#1b2a4a',
    'sapphire': '#0f3d82',
    'turquoise': '#2ab3b3',
    'aquamarine': '#7fd4d4',
    'sky blue': '#87ceeb',
    'purple': '#6b3fa0',
    'violet': '#7f4fc0',
    'amethyst': '#8f5fb0',
    'lavender': '#cbb5e0',
    'pink': '#e8799a',
    'rose': '#e0798f',
    'baby pink': '#f4c2cd',
    'magenta': '#c2185b',
    'peach': '#ffc9a3',
    'orange': '#e07b20',
    'coral': '#f08573',
    'yellow': '#e8c23a',
    'mustard': '#d4a017',
    'gold': '#d4af37',
    'antique gold': '#b89437',
    'victorian gold': '#a8842c',
    'rose gold': '#dfa295',
    'champagne': '#e6cfa8',
    'silver': '#c4c7c9',
    'rhodium': '#d2d5d8',
    'oxidised': '#6a6a6a',
    'oxidized': '#6a6a6a',
    'grey': '#8c8c8c',
    'gray': '#8c8c8c',
    'black': '#1c1c1c',
    'white': '#fbfbfb',
    'ivory': '#f4efe4',
    'pearl': '#f2ece1',
    'cream': '#f0e7d6',
    'beige': '#e3d5bd',
    'brown': '#6f4a2f',
    'copper': '#b87333',
    'bronze': '#a97142',
    'multicolour': 'linear-gradient(135deg,#c02026 0%,#e8c23a 34%,#2e7d4f 67%,#1f5fa9 100%)',
    'multicolor': 'linear-gradient(135deg,#c02026 0%,#e8c23a 34%,#2e7d4f 67%,#1f5fa9 100%)',
    'multi': 'linear-gradient(135deg,#c02026 0%,#e8c23a 34%,#2e7d4f 67%,#1f5fa9 100%)',
    'rainbow': 'linear-gradient(135deg,#c02026 0%,#e8c23a 34%,#2e7d4f 67%,#1f5fa9 100%)',
    'clear': 'linear-gradient(135deg,#ffffff 0%,#e8eef2 50%,#cfd8de 100%)',
    'crystal': 'linear-gradient(135deg,#ffffff 0%,#e8eef2 50%,#cfd8de 100%)',
    'ad stone': 'linear-gradient(135deg,#ffffff 0%,#e8eef2 50%,#cfd8de 100%)',
    'diamond': 'linear-gradient(135deg,#ffffff 0%,#e8eef2 50%,#cfd8de 100%)'
  };

  function cssKnows(value) {
    try {
      return !!(window.CSS && CSS.supports && CSS.supports('background-color', value));
    } catch (err) {
      return false;
    }
  }

  function lookup(label) {
    var key = String(label || '').trim().toLowerCase();
    if (!key) return null;
    if (COLOUR[key]) return COLOUR[key];

    /* "Green Amethyst Choker" style labels — try the trailing words. */
    var words = key.split(/\s+/);
    for (var take = 1; take < words.length; take++) {
      var hit = COLOUR[words.slice(take).join(' ')];
      if (hit) return hit;
    }
    return null;
  }

  /* Paint the swatches whose colour CSS cannot resolve on its own. Only
     touches inline styles and attributes, never the DOM tree. */
  function paintSwatches() {
    var nodes = document.querySelectorAll('.filter--color .color-filter--default');

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.hasAttribute('data-gou-swatch')) return;
      node.setAttribute('data-gou-swatch', '');

      /* An uploaded swatch image beats anything we could guess. */
      if (node.style.backgroundImage && node.style.backgroundImage !== 'none') return;

      var link = node.closest('a');
      var label = (link && link.getAttribute('title')) || '';
      var hit = lookup(label);

      if (hit) {
        node.style.background = hit;
        return;
      }

      /* Nothing matched, and if CSS cannot resolve the label either the
         swatch renders as a hole. Flag it so the CSS gives it a neutral chip
         with a line through it instead. */
      var lastWord = String(label).trim().toLowerCase().split(/\s+/).pop();
      if (!cssKnows(lastWord)) node.setAttribute('data-gou-swatch', 'unknown');
    });
  }

  /* ------------------------------------------------------------------------
     A colour filter Forge did not recognise as one.
     Forge only draws swatches when the filter's label appears in the theme's
     "Swatch option name" setting, so a filter named "Shop by Colour" comes
     out as a column of checkboxes with a jewellery icon. Find it by its label
     instead and promote it, so the naming in Shopify admin stops mattering.
     ------------------------------------------------------------------------ */
  var COLOUR_LABEL = /colou?r|shade|stone|metal/i;

  function upgradeColourFilter() {
    var blocks = document.querySelectorAll('.sidebar__filter-block:not(.filter--color)');

    Array.prototype.forEach.call(blocks, function (block) {
      if (block.hasAttribute('data-gou-colour')) return;

      var heading = block.querySelector('p.sm-caps');
      if (!heading || !COLOUR_LABEL.test(heading.textContent)) return;

      var rows = block.querySelectorAll('li.filter-item a.js-collection-side-filter');
      if (!rows.length) return;

      /* Only promote it when the values really do read as colours - at least
         half of them have to be in the table. Otherwise this is a filter that
         merely has "stone" or "metal" in its name. */
      var known = 0;
      Array.prototype.forEach.call(rows, function (row) {
        var text = row.querySelector('.regular-filter--label span');
        if (text && lookup(text.textContent)) known++;
      });
      if (known * 2 < rows.length) {
        block.setAttribute('data-gou-colour', 'no');
        return;
      }

      block.setAttribute('data-gou-colour', 'yes');
      block.classList.add('filter--color', 'gou-colour-upgraded');

      Array.prototype.forEach.call(rows, function (row) {
        var text = row.querySelector('.regular-filter--label span');
        var label = row.querySelector('.regular-filter--label');
        if (!text || !label || label.querySelector('.color-filter')) return;

        var chip = document.createElement('span');
        chip.className = 'color-filter color-filter--rounded color-filter--default';
        chip.setAttribute('data-gou-swatch', '');

        var hit = lookup(text.textContent);
        if (hit) {
          chip.style.background = hit;
        } else {
          chip.setAttribute('data-gou-swatch', 'unknown');
        }

        label.insertBefore(chip, label.firstChild);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Sort select in the collection toolbar
     ------------------------------------------------------------------------ */
  function sortOptionsFromDrawer() {
    var list = document.querySelector('[data-menu-handle="sort"] ul');
    if (!list) return [];

    /* Shopify offers "relevance" in the sort list even on a collection, where
       there is no query for it to be relevant to and picking it does nothing.
       Keep it only on a search results page. */
    var isSearch = !!new URLSearchParams(window.location.search).get('q');

    var out = [];
    Array.prototype.forEach.call(list.querySelectorAll('li'), function (li) {
      var input = li.querySelector('input[name="sort_by"]');
      var label = li.querySelector('label');
      if (!input || !label || !input.value) return;
      if (input.value === 'relevance' && !isSearch) return;
      out.push({
        value: input.value,
        label: label.textContent.replace(/\s+/g, ' ').trim(),
        current: li.classList.contains('current')
      });
    });
    return out;
  }

  var CHEVRON =
    '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
    '<path d="M2.5 4.5L6 8l3.5-3.5" fill="none" stroke="currentColor" ' +
    'stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function escapeAttr(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  function escapeText(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* Returns true when it changed the DOM, so the caller knows to re-arm the
     observer rather than looping on its own insertion. */
  function buildSortBar() {
    var topbar = document.querySelector('.collection__page--topbar');
    if (!topbar) return false;

    var urlSort = new URLSearchParams(window.location.search).get('sort_by') || '';
    var existing = topbar.querySelector('.gou-sortbar select');

    /* Already there and already showing the right option — leave it alone. */
    if (existing && existing.value === urlSort) return false;
    if (existing && !urlSort && existing.selectedIndex === 0) return false;

    var options = sortOptionsFromDrawer();
    if (!options.length) return false;

    var markup = '';
    var matched = false;
    options.forEach(function (option) {
      var selected = urlSort ? option.value === urlSort : option.current;
      if (selected) matched = true;
      markup +=
        '<option value="' + escapeAttr(option.value) + '"' +
        (selected ? ' selected' : '') + '>' + escapeText(option.label) + '</option>';
    });
    /* Nothing is selected, because no sort is in the URL and Liquid only marks
       an option "current" when one is. A neutral placeholder rather than a
       second "Featured", which would both duplicate the real option and claim
       a sort order the collection may not actually be using. */
    if (!matched) markup = '<option value="" selected>Sort by\u2026</option>' + markup;

    var old = topbar.querySelector('.gou-sortbar');
    if (old) old.parentNode.removeChild(old);

    var wrap = document.createElement('div');
    wrap.className = 'gou-sortbar';
    wrap.innerHTML =
      '<label class="gou-sortbar__label" for="gou-sort-select">Sort by</label>' +
      '<span class="gou-sortbar__field">' +
      '<select class="gou-sortbar__select" id="gou-sort-select" aria-label="Sort products">' +
      markup +
      '</select>' + CHEVRON + '</span>';

    /* Forge's right-hand toolbar cell holds the product count and is hidden on
       mobile. Reuse it so the count and the sort read as one row, and let it
       show on phones. */
    var cell = topbar.querySelector('.span-7');
    if (cell) {
      cell.classList.remove('sm-hide');
      cell.classList.add('gou-sortbar__cell', 'sm-span-12');
      cell.appendChild(wrap);
    } else {
      topbar.appendChild(wrap);
    }

    document.body.classList.add('gou-has-sortbar');
    return true;
  }

  /* ------------------------------------------------------------------------
     Applying a sort. A full navigation rather than Forge's AJAX path: that
     path re-opens the filter drawer as a side effect, and for a deliberate
     one-off action a clean shareable URL is worth more than 200ms.
     ------------------------------------------------------------------------ */
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || !select.classList || !select.classList.contains('gou-sortbar__select')) return;

    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    if (select.value) {
      params.set('sort_by', select.value);
    } else {
      params.delete('sort_by');
    }

    var qs = params.toString();
    window.location.search = qs ? '?' + qs : '';
  });

  /* ------------------------------------------------------------------------
     Keep the sort when a filter changes, and never send two sorts at once.
     Capture phase, so this runs before Forge's own handler on the <li> reads
     the form.
     ------------------------------------------------------------------------ */
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;

      var item = event.target.closest('li.js-filter');
      if (!item) return;

      var form = item.closest('form[data-filters]');
      if (!form) return;

      var stale = form.querySelector('input[data-gou-sort-keep]');
      if (stale) stale.parentNode.removeChild(stale);

      var sortBlock = form.querySelector('[data-menu-handle="sort"]');

      if (sortBlock && sortBlock.contains(item)) {
        /* A sort was picked: make sure it is the only one that goes out. */
        Array.prototype.forEach.call(
          sortBlock.querySelectorAll('input[name="sort_by"]'),
          function (input) {
            if (!item.contains(input)) input.checked = false;
          }
        );
        return;
      }

      /* A filter was picked: carry across whatever sort the URL already has. */
      var current = new URLSearchParams(window.location.search).get('sort_by');
      var alreadyChecked = sortBlock
        ? sortBlock.querySelector('input[name="sort_by"]:checked')
        : null;

      if (current && !alreadyChecked) {
        var keep = document.createElement('input');
        keep.type = 'hidden';
        keep.name = 'sort_by';
        keep.value = current;
        keep.setAttribute('data-gou-sort-keep', '');
        form.appendChild(keep);
      }
    },
    true
  );

  /* ------------------------------------------------------------------------
     Run, and run again after Forge swaps the grid over AJAX — that replaces
     the toolbar and the whole drawer along with it. The observer is
     disconnected while we work so our own insertion cannot re-trigger it.
     ------------------------------------------------------------------------ */
  var observer = null;
  var grid = null;
  var pending = null;

  function connect() {
    if (observer && grid) observer.observe(grid, { childList: true, subtree: true });
  }

  function refresh() {
    if (observer) observer.disconnect();
    try {
      upgradeColourFilter();
      paintSwatches();
      buildSortBar();
    } catch (err) {
      /* Never let a filter cosmetic break the page. */
    }
    connect();
  }

  function start() {
    grid = document.getElementById('CollectionProductGrid');

    if (grid && window.MutationObserver) {
      observer = new MutationObserver(function () {
        if (pending) clearTimeout(pending);
        pending = setTimeout(refresh, 40);
      });
    }

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
